import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { useProfile } from '@/hooks/useProfile'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import apiClient from '@/lib/api-client'
import { Loader2, Upload, Save, ImageIcon } from 'lucide-react'
import { useDashboardEmbedded } from '@/hooks/useDashboardEmbedded'

const profileSchema = z.object({
  business_name: z.string().min(1, 'Business name is required').max(150),
  tagline: z.string().max(80).optional(),
  bio: z.string().max(500).optional(),
  location: z.string().max(200).optional(),
  country_code: z.string().optional(),
  service_area: z.string().optional(),
  phone: z.string().max(30).optional(),
  email: z.string().email().optional(),
  address: z.string().optional(),
})

type ProfileForm = z.infer<typeof profileSchema>

export default function ProfileEditor() {
  const { t } = useTranslation('dashboard')
  const { profile, updateProfile } = useProfile()
  const { user } = useAuth()
  const embedded = useDashboardEmbedded()
  const [isSaving, setIsSaving] = useState(false)
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const [isUploadingCover, setIsUploadingCover] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      business_name: profile?.business_name || '',
      tagline: profile?.tagline || '',
      bio: profile?.bio || '',
      location: profile?.location || '',
      country_code: profile?.country_code || '',
      service_area: profile?.service_area || '',
      phone: profile?.phone || '',
      email: profile?.email || user?.email || '',
      address: profile?.address || '',
    },
  })

  useEffect(() => {
    if (profile) {
      reset({
        business_name: profile.business_name || '',
        tagline: profile.tagline || '',
        bio: profile.bio || '',
        location: profile.location || '',
        country_code: profile.country_code || '',
        service_area: profile.service_area || '',
        phone: profile.phone || '',
        email: profile.email || user?.email || '',
        address: profile.address || '',
      })
    }
  }, [profile, reset, user?.email])

  const onSubmit = async (data: ProfileForm) => {
    setIsSaving(true)
    try {
      await apiClient.put('/profile', data)
      await updateProfile()
    } catch (error) {
      console.error('Failed to save profile:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setIsUploadingImage(true)
    try {
      const formData = new FormData()
      formData.append('image', file)
      await apiClient.post('/user/profile/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      await updateProfile()
    } catch (error) {
      console.error('Failed to upload image:', error)
    } finally {
      setIsUploadingImage(false)
    }
  }

  const handleCoverUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setIsUploadingCover(true)
    try {
      const formData = new FormData()
      formData.append('image', file)
      await apiClient.post('/user/profile/cover-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      await updateProfile()
    } catch (error) {
      console.error('Failed to upload cover image:', error)
    } finally {
      setIsUploadingCover(false)
    }
  }

  const watchedValues = watch()

  return (
    <div className="p-6 lg:p-8 max-w-4xl">
      {!embedded && (
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{t('profile.settings.title')}</h1>
          <p className="text-gray-500">{t('profile.settings.subtitle')}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-6">
          {/* Profile Image */}
          <Card>
            <CardHeader>
              <CardTitle>{t('profile.photo.title')}</CardTitle>
              <CardDescription>
                {t('profile.photo.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                <div className="relative">
                  {profile?.profile_image ? (
                    <img
                      src={profile.profile_image}
                      alt="Profile"
                      className="w-24 h-24 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center">
                      <span className="text-2xl font-medium text-gray-400">
                        {watchedValues.business_name?.[0] || 'U'}
                      </span>
                    </div>
                  )}
                  {isUploadingImage && (
                    <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                      <Loader2 className="w-6 h-6 text-white animate-spin" />
                    </div>
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    id="profile-image"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                  <label htmlFor="profile-image">
                    <Button type="button" variant="outline" asChild>
                      <span>
                        <Upload className="w-4 h-4 mr-2" />
                        {t('profile.photo.upload')}
                      </span>
                    </Button>
                  </label>
                  <p className="mt-2 text-xs text-gray-500">
                    {t('profile.photo.uploadHint')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cover Image */}
          <Card>
            <CardHeader>
              <CardTitle>{t('profile.cover.title')}</CardTitle>
              <CardDescription>{t('profile.cover.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="relative w-full h-40 rounded-lg overflow-hidden bg-muted">
                  {profile?.cover_image ? (
                    <img
                      src={profile.cover_image}
                      alt="Cover"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-10 h-10 text-muted-foreground" />
                    </div>
                  )}
                  {isUploadingCover && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 text-white animate-spin" />
                    </div>
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    id="cover-image"
                    accept="image/*"
                    className="hidden"
                    onChange={handleCoverUpload}
                  />
                  <label htmlFor="cover-image">
                    <Button type="button" variant="outline" asChild>
                      <span>
                        <Upload className="w-4 h-4 mr-2" />
                        {t('profile.cover.upload')}
                      </span>
                    </Button>
                  </label>
                  <p className="mt-2 text-xs text-gray-500">{t('profile.cover.uploadHint')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>{t('profile.businessInfo.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.businessInfo.businessName')} *
                </label>
                <Input {...register('business_name')} />
                {errors.business_name && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.business_name.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.businessInfo.tagline')}
                </label>
                <Input
                  {...register('tagline')}
                  placeholder={t('profile.businessInfo.taglinePlaceholder')}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t('profile.businessInfo.taglineChars', { count: watchedValues.tagline?.length || 0 })}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.businessInfo.bio')}
                </label>
                <textarea
                  {...register('bio')}
                  rows={4}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder={t('profile.businessInfo.bioPlaceholder')}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {watchedValues.bio?.length || 0}/500 characters
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <Card>
            <CardHeader>
              <CardTitle>{t('profile.contactInfo.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.contactInfo.phone')}
                  </label>
                  <Input {...register('phone')} type="tel" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.contactInfo.email')}
                  </label>
                  <Input {...register('email')} type="email" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.contactInfo.location')}
                </label>
                <Input
                  {...register('location')}
                  placeholder={t('profile.contactInfo.locationPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.contactInfo.country')}
                </label>
                <select
                  {...register('country_code')}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value="">{t('profile.contactInfo.countryPlaceholder')}</option>
                  <option value="US">United States</option>
                  <option value="GB">United Kingdom</option>
                  <option value="DE">Germany</option>
                  <option value="FR">France</option>
                  <option value="ES">Spain</option>
                  <option value="IT">Italy</option>
                  <option value="NL">Netherlands</option>
                  <option value="BE">Belgium</option>
                  <option value="AT">Austria</option>
                  <option value="CH">Switzerland</option>
                  <option value="PL">Poland</option>
                  <option value="CZ">Czech Republic</option>
                  <option value="RO">Romania</option>
                  <option value="BG">Bulgaria</option>
                  <option value="GR">Greece</option>
                  <option value="PT">Portugal</option>
                  <option value="SE">Sweden</option>
                  <option value="NO">Norway</option>
                  <option value="DK">Denmark</option>
                  <option value="FI">Finland</option>
                  <option value="IE">Ireland</option>
                  <option value="AU">Australia</option>
                  <option value="NZ">New Zealand</option>
                  <option value="CA">Canada</option>
                  <option value="MX">Mexico</option>
                  <option value="BR">Brazil</option>
                  <option value="AR">Argentina</option>
                  <option value="JP">Japan</option>
                  <option value="KR">South Korea</option>
                  <option value="IN">India</option>
                  <option value="ZA">South Africa</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.contactInfo.serviceArea')}
                </label>
                <Input
                  {...register('service_area')}
                  placeholder={t('profile.contactInfo.serviceAreaPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.contactInfo.address')}
                </label>
                <Input
                  {...register('address')}
                  placeholder={t('profile.contactInfo.addressPlaceholder')}
                />
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('profile.save.saving')}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {t('profile.save.saveChanges')}
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}
